import { useState } from "react";
import { Link } from "react-router-dom";
import LeadFormModal from "../components/LeadFormModal";
import Reveal from "../components/Reveal";
import { BenefitIcon, pickBenefitIcon } from "../lib/benefitIcons";
import BookFooter from "../components/BookFooter";

// Colors a leading "FREE" the way the reference site does, without
// requiring headline to be rich text — it's still a single plain string
// coming from the admin form.
function renderHeadline(headline) {
  const match = headline.match(/^(free)\b(.*)$/i);
  if (!match) return headline;
  return (
    <>
      <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
        {match[1]}
      </span>
      {match[2]}
    </>
  );
}

const CTA_BUTTON =
  "group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 px-7 py-3.5 font-heading text-base font-semibold text-white shadow-lg shadow-sky-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-sky-500/35";

// Pure presentational component — the marketing/lead-capture landing page.
// Both /book/:slug and "/" (the latest published book) render this with
// whatever book they fetched.
export default function BookLandingView({ book }) {
  const [modalOpen, setModalOpen] = useState(false);
  const hasCover = Boolean(book.cover_image_url);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero pitch — the very top of the page. This is a pure marketing/
          lead-capture landing page: no live reader here. The reader only
          becomes reachable via the link handed out after the lead form is
          submitted (see /read/:slug). */}
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 via-cyan-50 to-white px-6 py-20 sm:py-28">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-sky-200/50 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 top-1/3 h-64 w-64 rounded-full bg-violet-200/30 blur-3xl"
        />

        <div
          className={`relative mx-auto flex max-w-5xl flex-col gap-8 sm:gap-10 ${
            hasCover ? "sm:flex-row sm:items-start" : "max-w-2xl text-center"
          }`}
        >
          {hasCover && (
            // Not wrapped in <Reveal> — this is above the fold and must be
            // visible immediately on load, not fade in after the
            // IntersectionObserver fires.
            <div className="book-showcase-stage relative flex flex-col items-center justify-center sm:shrink-0 sm:items-start">
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 m-auto h-72 w-56 -translate-x-2 translate-y-3 rotate-6 rounded-full bg-gradient-to-br from-sky-200 to-violet-200"
              />
              <img
                src={book.cover_image_url}
                alt={book.title}
                className="book-showcase relative max-h-96 w-auto sm:max-h-[30rem]"
              />
              <div
                aria-hidden
                className="book-showcase-shadow relative -mt-2 h-4 w-40 rounded-full bg-zinc-900 blur-md sm:w-48"
              />
            </div>
          )}
          <div className={hasCover ? "max-w-2xl" : ""}>
            <span className="font-heading inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-white/80 px-3.5 py-1.5 text-xs font-semibold text-sky-700 shadow-sm backdrop-blur-sm">
              🎁 Free Download
            </span>

            <h2 className="font-heading mt-4 text-4xl font-extrabold leading-tight tracking-tight text-zinc-900 sm:text-5xl">
              {renderHeadline(book.headline)}
            </h2>

            <div
              className={`mt-7 flex flex-wrap items-center gap-3 ${
                hasCover ? "" : "justify-center"
              }`}
            >
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className={CTA_BUTTON}
              >
                SEND ME A COPY FOR FREE
                <span className="transition-transform group-hover:translate-x-1">
                  →
                </span>
              </button>
              <Link
                to={`/books/${book.slug}/read`}
                className="font-heading rounded-xl border-2 border-zinc-900 px-7 py-3.5 text-base font-semibold text-zinc-900 transition-all duration-300 hover:-translate-y-0.5 hover:bg-zinc-900 hover:text-white"
              >
                Read Online
              </Link>
            </div>

            <div
              className={`mt-4 flex items-start gap-1.5 ${
                hasCover ? "" : "justify-center"
              }`}
            >
              <CurvedArrowIcon />
              <p className="font-hand max-w-[18rem] text-base leading-snug text-zinc-700">
                {book.subheadline}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits grid */}
      <section className="relative bg-gradient-to-b from-white to-zinc-50 px-6 py-20 text-center sm:py-28">
        <Reveal>
          <div className="font-hand flex items-center justify-center gap-2 text-amber-400">
            <span aria-hidden className="text-lg leading-none">
              ★★★★★
            </span>
            <span className="text-sm text-zinc-500">
              #1 in {book.category}
            </span>
          </div>
          <h3 className="font-heading mt-3 text-2xl font-extrabold text-zinc-900 sm:text-3xl">
            {book.title}
          </h3>

          {book.intro_body && (
            <p className="mx-auto mt-6 max-w-3xl whitespace-pre-line leading-relaxed text-zinc-700">
              {book.intro_body}
            </p>
          )}
        </Reveal>

        {/* Sneak peek screenshot — a static marketing image, separate from
            the live reader up top */}
        {book.preview_image_url && (
          <Reveal delay={100} className="relative mx-auto mt-12 max-w-4xl">
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-4 rounded-3xl bg-gradient-to-r from-sky-200/60 via-violet-200/40 to-amber-200/40 blur-2xl"
            />
            <div className="relative overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl">
              <div className="flex items-center gap-1.5 border-b border-zinc-100 bg-zinc-50 px-4 py-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              </div>
              <img
                src={book.preview_image_url}
                alt={`Preview of ${book.title}`}
                className="w-full"
              />
            </div>
            <div className="absolute -right-4 top-1/2 hidden -translate-y-1/2 translate-x-full flex-col items-start sm:flex">
              <span className="font-hand text-lg text-zinc-800">
                It's free!!
              </span>
              <FreeArrowIcon />
            </div>
          </Reveal>
        )}

        <div className="mx-auto mt-14 grid max-w-5xl gap-6 text-left sm:grid-cols-2 lg:grid-cols-4">
          {book.benefits.map((benefit, i) => (
            <Reveal key={benefit.title} delay={i * 80}>
              <div className="h-full rounded-2xl border border-zinc-100 bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-sky-50 to-violet-50">
                  <BenefitIcon
                    icon={pickBenefitIcon(benefit.icon, i)}
                    className="text-sky-600"
                  />
                </div>
                <h4 className="font-heading mt-4 text-lg font-bold text-zinc-900">
                  {benefit.title}
                </h4>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">
                  {benefit.description}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={150} className="mt-14">
          <Link
            to={`/books/${book.slug}/read`}
            className="font-heading inline-block rounded-xl border-2 border-zinc-900 bg-white px-7 py-3 text-sm font-semibold text-zinc-900 transition-all duration-300 hover:-translate-y-0.5 hover:bg-zinc-900 hover:text-white"
          >
            Read Online
          </Link>

          <div className="mt-6 flex items-start justify-center gap-1.5">
            <span className="font-hand text-base text-zinc-800">
              Click the button below to claim your free copy now.
            </span>
            <DownArrowIcon />
          </div>
        </Reveal>
      </section>

      {/* Closing pitch */}
      <section className="relative bg-gradient-to-br from-black via-zinc-950 to-black px-6 py-20 sm:py-28">
        {/* overflow-hidden deliberately NOT on the section — it would clip
            the book cover below, which is positioned to intentionally
            bleed above this section's top edge. */}
        <div
          aria-hidden
          className="pointer-events-none absolute right-0 top-0 h-96 w-96 overflow-hidden rounded-full bg-sky-500/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 overflow-hidden rounded-full bg-violet-500/10 blur-3xl"
        />
        <Reveal
          className={`relative mx-auto max-w-5xl ${
            hasCover ? "sm:max-w-xl" : "max-w-2xl text-center"
          }`}
        >
          <h3 className="font-heading text-2xl font-extrabold leading-tight text-amber-400 sm:text-3xl">
            {book.closing_heading}
          </h3>
          <p className="mt-4 whitespace-pre-line leading-relaxed text-zinc-300">
            {book.closing_body}
          </p>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className={`${CTA_BUTTON} mt-7`}
          >
            SEND ME A COPY FOR FREE
            <span className="transition-transform group-hover:translate-x-1">
              →
            </span>
          </button>
        </Reveal>

        {hasCover && (
          <>
            {/* Mobile: a normal, contained image below the text. */}
            <div className="mt-10 flex justify-center sm:hidden">
              <img
                src={book.cover_image_url}
                alt={book.title}
                className="max-h-80 w-auto rounded-lg shadow-2xl"
              />
            </div>

            {/* sm and up: positioned against the section itself, so it
                reliably pokes above the section's edge. */}
            <div className="pointer-events-none absolute right-6 top-0 hidden -translate-y-20 sm:right-12 sm:block lg:right-24">
              <img
                src={book.cover_image_url}
                alt={book.title}
                className="max-h-[28rem] w-auto rounded-lg shadow-2xl"
              />
            </div>
          </>
        )}
      </section>

      <Reveal>
        <BookFooter book={book} />
      </Reveal>

      <LeadFormModal
        book={book}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}

function DownArrowIcon() {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" className="mt-1 shrink-0 text-zinc-700">
      <circle cx="2" cy="4" r="1.8" fill="currentColor" />
      <path d="M4 6C10 8 18 14 24 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M15 22L25 24L23 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FreeArrowIcon() {
  return (
    <svg width="34" height="46" viewBox="0 0 34 46" fill="none" className="mt-1 text-zinc-800">
      <circle cx="30" cy="4" r="1.8" fill="currentColor" />
      <path d="M28 8C22 18 12 30 4 38" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M11 42L3 40L5 32" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CurvedArrowIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 30 30" fill="none" className="mt-1 shrink-0 text-zinc-700">
      <circle cx="26" cy="26" r="1.8" fill="currentColor" />
      <path d="M24 23C18 14 10 7 3 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M9 3L2 3.5L3.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
